import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuthContext } from "../../Context/AuthContext";
import { useEngineConfigContext } from "../../Context/EngineConfigContext";
import { useMyApiContext } from "../../Context/MyApiContext";
import { useSubscriptionAccess } from "../../Context/SubscriptionAccessContext";
import { getPersistedMyApisUiState } from "../../utils/myApisPersistence";
import {
  getPersistedHomeUiState,
  saveHomePersistence
} from "../../utils/homePersistence";
import DashboardLayout from "../../Layout/DashboardLayout";
import DashboardTopbar from "../../Layout/DashboardTopbar";
import useTokenBalance from "../../hooks/useTokenBalance";
import HomeStats from "./HomeStats";
import HomeQuickActions from "./HomeQuickActions";
import HomeSystemStatus from "./HomeSystemStatus";
import "./home-dashboard.css";

const Home = () => {
  const persistedUi = getPersistedHomeUiState();
  const myApisPersisted = getPersistedMyApisUiState();
  const { list } = useEngineConfigContext();
  const { list: myApiList, fetchList, listCount } = useMyApiContext();
  const {
    fetchUserAttributes,
    user,
    userAttributes: { length: userAttrLength }
  } = useAuthContext();
  const {
    checking: subscriptionChecking,
    status: subscriptionStatus,
    isRestrictedMenuDisabled,
    navigateWithSubscriptionCheck,
    refreshSubscriptionValidity
  } = useSubscriptionAccess();
  const { t } = useTranslation("common");
  const hasRestorableCache = persistedUi.hasCache || myApisPersisted.hasCache;
  const [fetching, setFetching] = useState(!hasRestorableCache);
  const [refreshing, setRefreshing] = useState(false);
  const { tokenBalance, tokensLoading } = useTokenBalance();

  useEffect(() => {
    if (!user) return undefined;

    let cancelled = false;

    const execute = async () => {
      const hasCache = hasRestorableCache;
      if (hasCache) {
        setRefreshing(true);
      } else {
        setFetching(true);
      }

      try {
        if (userAttrLength === 0) {
          await fetchUserAttributes();
        }
        await Promise.all([
          fetchList({ background: hasCache }),
          refreshSubscriptionValidity()
        ]);
      } finally {
        if (!cancelled) {
          setFetching(false);
          setRefreshing(false);
        }
      }
    };

    execute();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const apiCount = myApiList.length > 0 ? myApiList.length : persistedUi.apiCount;
  const engineCount = list.length > 0 ? list.length : persistedUi.engineCount;
  const usageCount =
    listCount > 0 || myApiList.length > 0 ? listCount : persistedUi.usageCount;
  const displayTokenBalance =
    tokensLoading && persistedUi.hasCache ? persistedUi.tokenBalance : tokenBalance;

  const isBlockingLoad = !hasRestorableCache && (fetching || tokensLoading);

  useEffect(() => {
    if (isBlockingLoad) return;

    saveHomePersistence({
      apiCount: myApiList.length > 0 ? myApiList.length : apiCount,
      engineCount: list.length > 0 ? list.length : engineCount,
      usageCount: listCount > 0 || myApiList.length > 0 ? listCount : usageCount,
      tokenBalance: tokensLoading ? displayTokenBalance : tokenBalance
    });
  }, [
    apiCount,
    engineCount,
    usageCount,
    tokenBalance,
    displayTokenBalance,
    isBlockingLoad,
    list.length,
    listCount,
    myApiList.length,
    tokensLoading
  ]);

  const showRefreshing = useMemo(
    () =>
      refreshing ||
      subscriptionChecking ||
      (hasRestorableCache && (fetching || tokensLoading)),
    [refreshing, subscriptionChecking, hasRestorableCache, fetching, tokensLoading]
  );

  const newApiDisabled = isRestrictedMenuDisabled("/api-generator");

  const handleNewApi = async (event) => {
    event.preventDefault();
    if (newApiDisabled || subscriptionChecking) return;
    await navigateWithSubscriptionCheck("/api-generator");
  };

  return (
    <DashboardLayout>
      <div className="dashboard-page home-dashboard">
        <DashboardTopbar title={t("Dashboard")} tokenBalance={tokenBalance}>
          {newApiDisabled ? (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled
              title={t("Renew your subscription to access this feature.")}>
              {t("+ New API")}
            </button>
          ) : (
            <a href="/api-generator" className="btn btn-primary btn-sm" onClick={handleNewApi}>
              {t("+ New API")}
            </a>
          )}
        </DashboardTopbar>
        <div className="page-content">
          {isBlockingLoad ? (
            <div className="dashboard-loading">{t("Fetching...")}</div>
          ) : (
            <>
              <div className="page-header">
                <h1>{t("Welcome back")}</h1>
                <p>{t("Here's a snapshot of your Odoo API workspace")}</p>
                {showRefreshing ? (
                  <p className="dashboard-loading" style={{ marginTop: 8 }}>
                    {subscriptionChecking
                      ? t("Checking subscription status...")
                      : t("Fetching...")}
                  </p>
                ) : null}
                {subscriptionStatus === "expired" ? (
                  <p className="home-subscription-warning">
                    {t(
                      "Your subscription has expired. Renew to use Engine Config, API Generator, and Get Token."
                    )}
                  </p>
                ) : null}
              </div>

              <HomeStats
                apiCount={apiCount}
                engineCount={engineCount}
                usageCount={usageCount}
                tokenBalance={displayTokenBalance}
                animate={!hasRestorableCache}
              />

              <div className="grid-2">
                <HomeQuickActions />
                <HomeSystemStatus usageCount={usageCount} tokenBalance={displayTokenBalance} />
              </div>
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Home;
